<template>
	<div
		class="relative flex min-h-full w-full flex-col bg-white lg:h-auto lg:min-h-full"
		:class="{ 'h-auto': isSuccessScreen }"
	>
		<div
			v-if="dialogs.headerFormModalOpen"
			class="fixed top-0 left-0 z-[3] h-full w-full bg-black/50"
		></div>

		<FormHeader :class="{ 'sticky top-0 z-[4]': !isSuccessScreen }" />

		<div
			class="mx-auto flex w-full flex-1 flex-col items-stretch lg:mt-6 lg:mb-4"
			:class="{ 'xs:px-4 overflow-hidden px-0': !isSuccessScreen }"
		>
			<component
				:is="currentFormComponent"
				class="flex-1 pt-12 pb-20 lg:my-6 lg:pt-0 lg:pb-28 lg:pb-3"
			/>
		</div>

		<div
			class="z-2 tall:lg:p-0 fixed bottom-0 bottom-0 left-0 left-0 w-full w-full bg-white px-5 pt-4 pb-5 shadow-lg lg:sticky lg:mx-0 lg:mb-0 lg:shadow-md"
		>
			<div
				class="z-2 tall:lg:py-8 container relative mx-auto flex max-w-5xl bg-white"
				:class="{
					'justify-center': !isBackVisible && uploadProgress === 0,
					'justify-between': isBackVisible && uploadProgress === 0,
					'justify-end': uploadProgress !== 0,
				}"
			>
				<div
					v-if="uploadProgress !== 0"
					class="text-primary/60 xs:mr-6 mr-2 flex cursor-pointer items-center justify-center whitespace-nowrap"
					@click="eventBus.emit({ name: EVENT_BUS_EVENTS.CANCEL_UPLOAD_IMAGES })"
				>
					<CloseSvg class="mr-2 h-3 w-3 fill-current" />
					Cancel Upload
				</div>

				<template v-else>
					<AppButton
						v-if="isBackVisible"
						class="xs:block hidden min-w-[190px]"
						:disabled="isProcessing"
						appearance="Outlined"
						@click="onPrevStep"
					>
						<span class="absolute top-3.5 left-4 lg:left-5">
							<BackSvg
								class="fill-success-base group-hover:fill-success-light group-disabled:fill-secondary h-5"
							/>
						</span>
						BACK
					</AppButton>

					<div
						v-if="isBackVisible"
						class="xs:hidden text-md text-main-400 hover:text-main-300 group flex min-w-[110px] basis-1/3 cursor-pointer items-center gap-3 py-2 pr-5 font-bold"
						@click="onPrevStep"
					>
						<span>
							<BackSvg
								class="fill-main-400 group-hover:fill-main-300 group-disabled:fill-secondary h-4"
							/>
						</span>
						BACK
					</div>
				</template>

				<AppButton
					v-if="isNextVisible"
					class="xs:basis-0 text-md xs:text-base min-w-[190px] overflow-hidden"
					:class="{ 'basis-2/3': uploadProgress === 0 }"
					:disabled="!isStepValid || isProcessing || uploadProgress !== 0"
					cta
					size="Normal"
					@click="onNextStep"
				>
					<span v-if="!isProcessing" class="z-2">
						{{
							uploadProgress === 0
								? nextButtonText
								: grid.md
								? 'Uploading images'
								: 'Uploading'
						}}
					</span>
					<div
						v-if="uploadProgress !== 0"
						class="bg-success-base z-1 absolute left-0 -top-0.5 h-[107%] w-0 transition-[0.2s]"
						:style="`width:${uploadProgress * 100}%`"
					/>

					<SpinnerSvg v-if="isProcessing" class="fill-white" />
				</AppButton>

				<div
					v-if="!isBackVisible"
					class="flex grow flex-wrap justify-around lg:flex-nowrap"
				>
					<template v-for="(button, index) in buttons" :key="`${index}-button`">
						<AppButton
							class="!text-12 mt-2 mb-2 w-[30%] whitespace-nowrap md:!min-w-[220px] lg:!min-w-[320px] lg:!text-lg"
							appearance="Outlined"
							size="Large"
							@click="onSubmitConfirmation(button.link)"
						>
							{{ button.title }}
						</AppButton>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { computed, ref, watch } from 'vue'
	import FormHeader from './components/FormHeader.vue'
	import * as StepComponents from './steps'
	import { Steps, useFormStore } from '../../stores/form'
	import AppButton from '../common/AppButton.vue'
	import SpinnerSvg from './components/icons/SpinnerSvg.vue'
	import { useDialogStore } from '../../stores/dialog'
	import BackSvg from './components/icons/BackSvg.vue'
	import dayjs from 'dayjs'
	import { LinkButton, useConfigStore } from '../../stores/config'
	import { EVENT_BUS_EVENTS, useEventBus } from '../../stores/eventBus'
	import CloseSvg from './components/icons/CloseSvg.vue'
	import { useAppScreen } from '../../services/app-screen'

	// hooks
	const config = useConfigStore()
	const form = useFormStore()
	const dialogs = useDialogStore()
	const eventBus = useEventBus()
	const { grid } = useAppScreen()

	// refs
	const isProcessing = ref(false)
	const uploadProgress = ref(0)

	eventBus.$onAction(({ args }) => {
		if (args[0].name === EVENT_BUS_EVENTS.UPLOAD_IMAGES_PROGRESS)
			uploadProgress.value = args[0].data as number

		if (args[0].name === EVENT_BUS_EVENTS.FORM_NEXT_STEP) onNextStep()
	})

	// constants
	const buttons = config.getConfigContent<LinkButton[]>('congratsButtons', [])
	const MAX_MILEAGE_PER_YEAR = 40000
	const MIN_MILEAGE_PER_YEAR = 5000

	watch(
		() => form.trim,
		() => {
			if (dialogs.trimsModalOpen) {
				return
			}
			form.mileage = ''
			form.aftermarkets = false
			form.aftermarketsComment = ''
			form.rateCondition = false
			form.quizList = []
			form.quizItems = []
			form.moreDamagedList = []
			form.moreDamagedItems = []
			form.mechIssueList = []
			form.mechIssueItems = []
			form.cosmIssueList = []
			form.cosmIssueItems = []
			form.images = []
		},
	)

	// computed
	const currentFormComponent = computed(() => {
		switch (form.currentStep.id) {
			case Steps.ContactInfo: {
				return StepComponents.ContactInfo
			}
			case Steps.AboutYourRide: {
				return StepComponents.AboutYourRide
			}
			case Steps.Trims: {
				return StepComponents.Trims
			}
			case Steps.Mileage: {
				return StepComponents.Mileage
			}
			case Steps.Aftermarket: {
				return StepComponents.Aftermarket
			}
			case Steps.RateCondition: {
				return StepComponents.RateCondition
			}
			case Steps.StandardQuiz:
			case Steps.DamagedQuiz: {
				return StepComponents.Quiz
			}
			case Steps.MoreDamagedOptions: {
				return StepComponents.MoreDamagedOptions
			}
			case Steps.MechanicalIssues: {
				return StepComponents.MechanicalIssues
			}
			case Steps.CosmeticIssues: {
				return StepComponents.CosmeticIssues
			}
			case Steps.SnapYourRide: {
				return StepComponents.SnapYourRide
			}
			case Steps.WhatDoYouWant: {
				return StepComponents.WhatDoYouWant
			}
			case Steps.SelectLocation: {
				return StepComponents.SelectLocation
			}
			case Steps.Congrats: {
				return StepComponents.Congrats
			}
			case Steps.Rejected: {
				return StepComponents.Rejected
			}
			case Steps.NeedChat: {
				return StepComponents.NeedChat
			}
			default:
				return StepComponents.ContactInfo
		}
	})

	const nextButtonText = computed(() => {
		switch (form.currentStep.id) {
			case Steps.ContactInfo:
				return 'GET MY OFFER'
			case Steps.SnapYourRide:
				return form.images.length ? 'CONTINUE' : 'SKIP'
			default:
				return 'CONTINUE'
		}
	})

	const isSuccessScreen = computed(() => {
		return form.currentStep.id === Steps.Congrats
	})

	const isBackVisible = computed(() => {
		switch (form.currentStep.id) {
			case Steps.Congrats:
			case Steps.NeedChat: {
				return false
			}
			default:
				return true
		}
	})

	const isNextVisible = computed(() => {
		switch (form.currentStep.id) {
			case Steps.Congrats:
			case Steps.Rejected:
			case Steps.NeedChat: {
				return false
			}
			default:
				return true
		}
	})

	const isStepValid = computed(() => {
		if (!Array.isArray(form.currentStep.valid)) return form.currentStep.valid
		return form.currentStep.valid?.map(v => form[v]).every(v => v)
	})

	// methods
	// Click prev button handler
	const onPrevStep = () => {
		if (form.hasPrevStep) {
			form.currentStep.back && form[form.currentStep.back]
				? form[form.currentStep.back]()
				: form.prevStep()
		} else {
			dialogs.vehicleOpen = false
		}
	}

	const onNextStep = () => {
		if (!isStepValid.value || isProcessing.value || uploadProgress.value !== 0) return

		if (form.currentStep.id === Steps.Mileage) {
			checkMileageValue(form.mileage.replace(/[\s.,%]/g, ''))
			if (dialogs.mileageErrorData.isModalOpen) {
				return
			}
		}
		isProcessing.value = true
		form[form.currentStep.action]()
			.catch(err => {
				console.log('Form submit error: ', err)

				if (err?.code === 'forbidden_make') {
					form.setStep(Steps.Rejected)
				}
			})
			.finally(() => (isProcessing.value = false))
	}

	const onSubmitConfirmation = (link = '') => {
		if (link) {
			location.href = link
		} else {
			dialogs.vehicleOpen = false
		}
	}

	const checkMileageValue = (value: string) => {
		if (!dialogs.mileageErrorData.wasModalOpen) {
			const currentYear = dayjs().get('year')
			const vehicleYear = form.year
			const yearsOld = +currentYear - +vehicleYear
			dialogs.mileageErrorData.years = yearsOld
			dialogs.mileageErrorData.mileage = +value
			const tooMuch = yearsOld >= 1 && +value > yearsOld * MAX_MILEAGE_PER_YEAR
			if (tooMuch) {
				dialogs.mileageErrorData.isTooMuch = true
			}
			const tooLow = yearsOld >= 1 && +value < yearsOld * MIN_MILEAGE_PER_YEAR
			if (tooLow) {
				dialogs.mileageErrorData.isTooMuch = false
			}
			if (tooMuch || tooLow) {
				dialogs.mileageErrorData.isModalOpen = true
				dialogs.mileageErrorData.wasModalOpen = true
				return false
			}
		}
	}
</script>
